import { Fragment, useCallback, useEffect, useState } from 'react';
import { api, fmt, fmtDate, fmtTimestamp } from '../api.ts';
import ConfirmDialog from './ConfirmDialog.tsx';
import UndoDialog from './UndoDialog.tsx';

/*
 * Monthly board stock check (062).
 *
 * Start a check, type what each board counts to, give a reason where it is
 * off, finish. Each count is saved as it is typed (on leaving the field), and
 * compared with the system figure at that moment, so the floor can keep
 * working while the store is counted. Finishing records the corrections.
 */

interface Line {
  item_id: number; name: string; on_hand: string; paired: string;
  counted: string | null; system_at_count: string | null; reason: string | null;
  counted_by_name: string | null; avg_price: string | null;
}
interface Open { id: number; started_at: string; started_by_name: string; lines: Line[] }
interface Past {
  check_id: number; finished_at: string; finished_by_name: string | null;
  boards_counted: string; boards_off: string; sheets_difference: string; sheets_off: string;
  value_difference: string | null;
}
type Draft = { counted: string; reason: string };

const signed = (n: number) => (n > 0 ? `+${fmt(String(n))}` : n < 0 ? `−${fmt(String(-n))}` : fmt(String(n)));
const localDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const rupees = (n: number) => {
  const a = Math.abs(n);
  const step = a >= 1000 ? 100 : a >= 100 ? 10 : 1;
  return `₹${(Math.round(a / step) * step).toLocaleString('en-IN')}`;
};

interface BoardOff { itemId: number; name: string; system: number; counted: number; reason: string }

function BoardsOffList({ boards }: { boards: BoardOff[] }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 8, background: 'var(--panel)' }}>
      {boards.map((b, i) => {
        const diff = b.counted - b.system;
        return (
          <div key={b.itemId} style={{
            display: 'grid', gridTemplateColumns: '1fr auto 64px', gap: 12, alignItems: 'baseline',
            padding: '9px 12px', borderTop: i ? '1px solid var(--line)' : undefined,
          }}>
            <div>
              <div style={{ fontSize: 13.5 }}>{b.name}</div>
              {b.reason && <div className="hint" style={{ marginTop: 2 }}>{b.reason}</div>}
            </div>
            <div className="dim" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              {fmt(String(b.system))} → <strong style={{ color: 'var(--ink)' }}>{fmt(String(b.counted))}</strong>
            </div>
            <div style={{ textAlign: 'right', fontWeight: 600, color: diff < 0 ? 'var(--late)' : 'var(--ok)' }}>
              {signed(diff)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function BoardStockCheck({ canManage, setError, setNotice, onChanged }: {
  canManage: boolean;
  setError: (s: string) => void;
  setNotice: (s: string) => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<{ open: Open | null; history: Past[] } | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState<'finish' | 'cancel' | null>(null);
  const [shown, setShown] = useState<number | null>(null);
  const [boardsOff, setBoardsOff] = useState<Record<number, BoardOff[]>>({});
  const [reopening, setReopening] = useState<number | null>(null);

  async function toggle(checkId: number) {
    if (shown === checkId) { setShown(null); return; }
    setShown(checkId);
    if (boardsOff[checkId]) return;
    try {
      const r = await api.get<{ boards: {
        item_id: number; name: string; system_at_count: string; counted: string; reason: string | null;
      }[] }>(`/api/stock/board-check/${checkId}/boards-off`);
      setBoardsOff((p) => ({ ...p, [checkId]: r.boards.map((b) => ({
        itemId: b.item_id, name: b.name, system: Number(b.system_at_count), counted: Number(b.counted),
        reason: b.reason ?? '',
      })) }));
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not load that stock check'); }
  }

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ open: Open | null; history: Past[] }>('/api/stock/board-check');
      setData(d);
      setDrafts(Object.fromEntries((d.open?.lines ?? []).map((l) => [l.item_id, {
        counted: l.counted === null ? '' : String(Number(l.counted)), reason: l.reason ?? '',
      }])));
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not load the stock check'); }
  }, [setError]);

  useEffect(() => { void load(); }, [load]);

  async function start() {
    setBusy(true); setError('');
    try { await api.post('/api/stock/board-check', {}); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not start a stock check'); }
    finally { setBusy(false); }
  }

  /** Saved on leaving a field, only when something changed. */
  async function save(l: Line) {
    if (!data?.open) return;
    const d = drafts[l.item_id]!;
    const was = { counted: l.counted === null ? '' : String(Number(l.counted)), reason: l.reason ?? '' };
    if (d.counted.trim() === was.counted && d.reason.trim() === was.reason) return;
    const n = Number(d.counted);
    if (d.counted.trim() !== '' && !(Number.isInteger(n) && n >= 0)) {
      setError(`${l.name}: the count is whole sheets`);
      return;
    }
    setError('');
    try {
      await api.put(`/api/stock/board-check/${data.open.id}/items/${l.item_id}`, {
        counted: d.counted.trim() === '' ? null : n,
        ...(d.reason.trim() ? { reason: d.reason.trim() } : {}),
      });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save the count'); }
  }

  async function finish() {
    if (!data?.open) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<{ boardsCounted: number; corrections: number }>(
        `/api/stock/board-check/${data.open.id}/finish`, {});
      setNotice(`Stock check finished · ${r.boardsCounted} counted · `
        + (r.corrections ? `${r.corrections} corrected` : 'all matched'));
      await load();
      onChanged();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not finish the stock check'); }
    finally { setBusy(false); setAsking(null); }
  }

  async function cancel() {
    if (!data?.open) return;
    setBusy(true); setError('');
    try {
      await api.post(`/api/stock/board-check/${data.open.id}/cancel`, {});
      setNotice('Stock check cancelled · nothing in stock changed');
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not cancel the stock check'); }
    finally { setBusy(false); setAsking(null); }
  }

  if (!data) return <div className="empty">Loading…</div>;

  // ---------------------------------------------------------- no open check
  if (!data.open) {
    const last = data.history[0];
    const thisMonth = last && localDay(last.finished_at).slice(0, 7) === localDay(new Date().toISOString()).slice(0, 7);
    return (
      <div style={{ padding: '4px 16px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 16px' }}>
          {!last && <span className="dim" style={{ fontSize: 13 }}>No stock check yet</span>}
          {!thisMonth && <span className="tag waiting">Due this month</span>}
          <span style={{ marginLeft: 'auto' }} />
          {canManage && (
            <button className="btn primary" disabled={busy} onClick={() => void start()}>Start stock check</button>
          )}
        </div>
        {data.history.length > 0 && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Finished</th><th>By</th><th className="num">Boards off</th>
                  <th className="num">Short</th><th className="num">Over</th><th className="num">Approx. value</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {data.history.map((h, i) => {
                  const offBoards = Number(h.boards_off);
                  const short = (Number(h.sheets_off) - Number(h.sheets_difference)) / 2;
                  const over = (Number(h.sheets_off) + Number(h.sheets_difference)) / 2;
                  const open = shown === Number(h.check_id);
                  return (
                    <Fragment key={h.check_id}>
                      <tr onClick={offBoards ? () => void toggle(Number(h.check_id)) : undefined}
                          style={{ cursor: offBoards ? 'pointer' : undefined }}>
                        <td title={fmtTimestamp(h.finished_at)}>
                          {offBoards > 0 && (
                            <span className="dim" style={{ display: 'inline-block', width: 14 }}>{open ? '▾' : '▸'}</span>
                          )}
                          {fmtDate(localDay(h.finished_at))}
                        </td>
                        <td className="dim">{h.finished_by_name ?? '—'}</td>
                        <td className="num">
                          {offBoards ? `${fmt(h.boards_off)} of ${fmt(h.boards_counted)}`
                            : <span className="dim">All {fmt(h.boards_counted)} matched</span>}
                        </td>
                        <td className="num" style={{ color: short ? 'var(--late)' : undefined }}>
                          {short ? signed(-short) : <span className="dim">—</span>}
                        </td>
                        <td className="num" style={{ color: over ? 'var(--ok)' : undefined }}>
                          {over ? signed(over) : <span className="dim">—</span>}
                        </td>
                        <td className="num dim">
                          {h.value_difference !== null && Number(h.value_difference) !== 0
                            ? `${Number(h.value_difference) < 0 ? '−' : '+'}${rupees(Number(h.value_difference))}` : '—'}
                        </td>
                        {canManage && (
                          <td className="num">
                            {i === 0 && (
                              <button className="btn"
                                      onClick={(e) => { e.stopPropagation(); setReopening(Number(h.check_id)); }}>
                                Reopen
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={canManage ? 7 : 6} style={{ padding: '4px 12px 14px', background: 'var(--panel-2)' }}>
                            {boardsOff[Number(h.check_id)]
                              ? <BoardsOffList boards={boardsOff[Number(h.check_id)]!} />
                              : <span className="dim" style={{ fontSize: 13 }}>Loading…</span>}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {reopening !== null && (
          <UndoDialog previewUrl={`/api/stock/board-check/${reopening}/reopen`}
                      onClose={() => setReopening(null)}
                      onDone={(msg) => { setNotice(msg); void load(); onChanged(); }} />
        )}
      </div>
    );
  }

  // --------------------------------------------------------------- counting
  const rows = data.open.lines.map((l) => {
    const d = drafts[l.item_id] ?? { counted: '', reason: '' };
    // Before a count is saved the figure to beat is the system's now; after,
    // the figure when it was counted.
    const system = Number(l.system_at_count ?? l.on_hand);
    const counted = d.counted.trim() === '' ? null : Number(d.counted);
    const diff = counted === null || !Number.isFinite(counted) ? null : counted - system;
    return { l, d, system, counted, diff };
  });
  const counted = rows.filter((r) => r.l.counted !== null).length;
  const sheetsOff = rows.reduce((t, r) => t + Math.abs(r.diff ?? 0), 0);
  const value = rows.reduce((t, r) => t + (r.diff ?? 0) * Number(r.l.avg_price ?? 0), 0);
  const missingReason = rows.some((r) => r.diff !== null && r.diff !== 0 && !r.d.reason.trim());

  return (
    <div style={{ padding: '4px 16px 16px' }}>
      <div className="dim" style={{ fontSize: 13, margin: '10px 0 12px' }}>
        Stock check · started {fmtTimestamp(data.open.started_at)} by {data.open.started_by_name}
        {' · '}{counted} of {rows.length} counted
      </div>
      <div className="tbl">
        {/* Fits the panel rather than scrolling sideways: names wrap, inputs shrink. */}
        <table style={{ tableLayout: 'fixed', minWidth: 0 }}>
          <thead>
            <tr>
              <th style={{ width: '32%' }}>Board</th>
              <th className="num" style={{ width: '11%' }}>System</th>
              <th className="num" style={{ width: '14%' }}>Counted</th>
              <th className="num" style={{ width: '11%' }}>Difference</th>
              <th style={{ paddingLeft: 16 }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ l, d, system, counted: c, diff }) => (
              <tr key={l.item_id}>
                <td style={{ whiteSpace: 'normal' }}>
                  {l.name}
                  {c !== null && c < Number(l.paired) && (
                    <div className="hint" style={{ color: 'var(--soon)' }}>
                      Below the {fmt(l.paired)} held for jobs — they will show short
                    </div>
                  )}
                </td>
                <td className="num">{fmt(String(system))}</td>
                <td className="num">
                  <input type="number" min="0" step="1" aria-label={`Counted ${l.name}`}
                         style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', textAlign: 'right' }}
                         value={d.counted} placeholder="—"
                         onChange={(e) => setDrafts((p) => ({ ...p, [l.item_id]: { ...d, counted: e.target.value } }))}
                         onBlur={() => void save(l)}
                         onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                </td>
                <td className="num" style={{
                  color: diff === null ? 'var(--muted)' : diff === 0 ? 'var(--ok)' : 'var(--late)',
                }}>
                  {diff === null ? '—' : diff === 0 ? '0' : signed(diff)}
                </td>
                <td style={{ paddingLeft: 16 }}>
                  {diff !== null && diff !== 0 && (
                    <input type="text" aria-label={`Why ${l.name} is off`} placeholder="Why is it off?"
                           style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }} value={d.reason}
                           onChange={(e) => setDrafts((p) => ({ ...p, [l.item_id]: { ...d, reason: e.target.value } }))}
                           onBlur={() => void save(l)}
                           onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <span className="dim" style={{ fontSize: 13 }}>
          {sheetsOff === 0 ? 'No differences so far'
            : `Off by ${fmt(String(sheetsOff))} sheets${value ? ` · about ${rupees(value)}` : ''}`}
          {missingReason && ' · give a reason for each board that is off'}
        </span>
        <span style={{ marginLeft: 'auto' }} />
        {canManage && (
          <>
            <button className="linkish" onClick={() => setAsking('cancel')}>Cancel check</button>
            <button className="btn primary" disabled={busy || counted === 0 || missingReason}
                    onClick={() => setAsking('finish')}>
              {busy ? 'Finishing…' : 'Finish check'}
            </button>
          </>
        )}
      </div>

      {asking === 'finish' && (() => {
        const off = rows.filter((r) => r.diff !== null && r.diff !== 0);
        return (
          <ConfirmDialog
            title="Finish stock check?"
            subtitle={`${counted} of ${rows.length} boards counted`}
            confirmLabel={off.length ? `Correct ${off.length} board${off.length === 1 ? '' : 's'} and finish` : 'Finish check'}
            busy={busy} onConfirm={() => void finish()} onCancel={() => setAsking(null)}>
            {off.length ? (
              <>
                <div style={{ fontSize: 13.5, marginBottom: 10 }}>Stock will be corrected to your count:</div>
                <BoardsOffList boards={off.map(({ l, d, system, counted: c }) => ({
                  itemId: l.item_id, name: l.name, system, counted: c!, reason: d.reason.trim(),
                }))} />
                {value !== 0 && (
                  <div className="hint" style={{ marginTop: 10 }}>
                    About {rupees(value)} {value < 0 ? 'less' : 'more'} in stock value.
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13.5 }}>Every board you counted matches. Nothing in stock changes.</div>
            )}
          </ConfirmDialog>
        );
      })()}

      {asking === 'cancel' && (
        <ConfirmDialog
          title="Cancel this stock check?"
          confirmLabel="Cancel check" cancelLabel="Keep counting"
          busy={busy} onConfirm={() => void cancel()} onCancel={() => setAsking(null)}>
          <div style={{ fontSize: 13.5 }}>
            Nothing in stock changes. The counts typed so far are kept in the record.
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
